import React, { useEffect, useMemo, useState } from "react";
import { Plus, ClipboardList, Users as UsersIcon, Filter, X } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { useI18n } from "../lib/i18n.jsx";
import { useAuth } from "../lib/auth.jsx";
import ConfigurableTable, { ColumnConfigButton, useColumnConfig } from "../components/ConfigurableTable.jsx";
import SavedViewsBar from "../components/SavedViewsBar.jsx";
import CompleteTaskDialog from "../components/CompleteTaskDialog.jsx";
import { tasksRowClass } from "../lib/columnConfig.js";

const MANAGER_ROLES = ["admin", "national_aftersales_manager", "department_manager"];

export default function TasksPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);

  const [view, setView] = useState("mine"); // 'mine' | 'all'
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [enums, setEnums] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [completeTask, setCompleteTask] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    branch: "",
    overdue: false,
    min_escalation: "",
    standalone_only: false,
  });
  const [selected, setSelected] = useState(new Set());
  const [showBulkAssign, setShowBulkAssign] = useState(false);

  const colConfig = useColumnConfig("tasks");

  useEffect(() => {
    api.get("/enums").then((r) => setEnums(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (view === "mine") params.append("mine", "true");
    if (filters.status) params.append("status", filters.status);
    if (filters.priority) params.append("priority", filters.priority);
    if (filters.branch) params.append("branch", filters.branch);
    if (filters.overdue) params.append("overdue", "true");
    if (filters.min_escalation !== "") params.append("min_escalation", filters.min_escalation);
    if (filters.standalone_only) params.append("standalone_only", "true");
    try {
      const { data } = await api.get(`/tasks?${params}`);
      setTasks(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setSelected(new Set()); // clear selection when filters / view change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, JSON.stringify(filters)]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    const eligibleIds = tasks.filter((t) => !["Concluída", "Cancelada"].includes(t.status)).map((t) => t.id);
    setSelected((prev) => prev.size === eligibleIds.length ? new Set() : new Set(eligibleIds));
  };
  const allEligible = tasks.filter((t) => !["Concluída", "Cancelada"].includes(t.status)).length;
  const allSelected = selected.size > 0 && selected.size === allEligible;

  const rowContext = useMemo(() => ({
    isManager,
    selected,
    toggleOne,
    onCompleteTask: setCompleteTask,
  }), [isManager, selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    return {
      total: tasks.length,
      overdue: tasks.filter((x) => x.escalation_level > 0).length,
      critical: tasks.filter((x) => x.escalation_level >= 5).length,
      standalone: tasks.filter((x) => !x.linked_case).length,
    };
  }, [tasks]);

  const clearFilters = () => setFilters({ status: "", priority: "", branch: "", overdue: false, min_escalation: "", standalone_only: false });
  const hasActiveFilters = Object.values(filters).some((v) => v && v !== "");

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E]">{t("tasks")}</h1>
          <p className="text-sm text-[#6C757D] mt-1">
            {counts.total} task{counts.total === 1 ? "" : "s"} ·{" "}
            <span className="text-[#DC3545]">{counts.overdue} overdue</span>
            {counts.critical > 0 && <> · <span className="text-[#DC3545] font-semibold">{counts.critical} critical</span></>}
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-2 text-sm font-medium flex items-center gap-2"
          data-testid="new-standalone-task-btn"
        >
          <Plus className="w-4 h-4" /> New task
        </button>
      </div>

      {/* View tabs */}
      <div className="border-b border-gray-200 flex gap-1 mb-4">
        <ViewTab
          active={view === "mine"}
          onClick={() => setView("mine")}
          icon={ClipboardList}
          label="My Tasks"
          testid="tasks-view-mine"
        />
        {isManager && (
          <ViewTab
            active={view === "all"}
            onClick={() => setView("all")}
            icon={UsersIcon}
            label="All Tasks"
            testid="tasks-view-all"
            badgeText="Manager"
          />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex flex-wrap items-end gap-3">
        <Filt label="Status">
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={inputCls} data-testid="filter-status">
            <option value="">All</option>
            {(enums?.task_statuses || []).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Filt>
        <Filt label="Priority">
          <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className={inputCls} data-testid="filter-priority">
            <option value="">All</option>
            {(enums?.priorities || []).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Filt>
        <Filt label="Branch">
          <select value={filters.branch} onChange={(e) => setFilters({ ...filters, branch: e.target.value })} className={inputCls} data-testid="filter-branch">
            <option value="">All</option>
            {(enums?.branches || []).map((s) => <option key={s}>{s}</option>)}
          </select>
        </Filt>
        <Filt label="Min escalation">
          <select value={filters.min_escalation} onChange={(e) => setFilters({ ...filters, min_escalation: e.target.value })} className={inputCls} data-testid="filter-escalation">
            <option value="">Any</option>
            <option value="1">L1+ (24h overdue)</option>
            <option value="2">L2+ (48h)</option>
            <option value="3">L3+ (72h)</option>
            <option value="4">L4+ (96h)</option>
            <option value="5">L5 critical (7d+)</option>
          </select>
        </Filt>
        <label className="flex items-center gap-1.5 text-sm pb-1">
          <input type="checkbox" checked={filters.overdue} onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })} data-testid="filter-overdue" />
          Overdue only
        </label>
        <label className="flex items-center gap-1.5 text-sm pb-1">
          <input type="checkbox" checked={filters.standalone_only} onChange={(e) => setFilters({ ...filters, standalone_only: e.target.checked })} data-testid="filter-standalone" />
          Standalone only
        </label>
        {hasActiveFilters && (
          <button onClick={clearFilters} className="ml-auto text-xs text-[#1F3864] hover:underline flex items-center gap-1" data-testid="clear-filters">
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
      </div>

      {/* Bulk action bar (manager only, when items selected) */}
      {isManager && selected.size > 0 && (
        <div className="bg-[#1F3864] text-white rounded-md px-3 py-2 mb-3 flex items-center gap-3" data-testid="bulk-bar">
          <span className="text-sm font-medium">{selected.size} task{selected.size === 1 ? "" : "s"} selected</span>
          <button onClick={() => setShowBulkAssign(true)} className="ml-auto bg-white text-[#1F3864] px-3 py-1 rounded-sm text-xs font-semibold hover:bg-[#F4F6F8]" data-testid="bulk-assign-open">
            Reassign…
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-[#CBD5E1] hover:text-white" data-testid="bulk-clear">Clear</button>
        </div>
      )}

      {/* Saved views + column config */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <SavedViewsBar viewKey="tasks" config={colConfig} />
        <div className="flex items-center gap-2">
          {isManager && tasks.length > 0 && (
            <button
              onClick={toggleAll}
              className="inline-flex items-center gap-1 text-xs border border-gray-300 hover:border-[#1F3864] rounded-sm px-2 py-1 text-[#1F3864]"
              data-testid="bulk-select-all"
              title="Select all eligible (not Completed/Cancelled)"
            >
              <input type="checkbox" checked={allSelected} readOnly className="pointer-events-none" />
              {allSelected ? "Limpar selecção" : "Seleccionar todas"}
            </button>
          )}
          <ColumnConfigButton config={colConfig} viewKey="tasks" />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-sm text-[#6C757D]">A carregar…</div>
      ) : (
        <ConfigurableTable
          viewKey="tasks"
          config={colConfig}
          rows={tasks}
          emptyMessage="Não existem tarefas que correspondam aos filtros actuais."
          rowContext={rowContext}
          getRowClassName={tasksRowClass}
        />
      )}

      {completeTask && <CompleteTaskDialog task={completeTask} onClose={() => setCompleteTask(null)} onDone={() => { setCompleteTask(null); load(); }} />}
      {showNew && enums && <NewTaskDialog enums={enums} users={users} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
      {showBulkAssign && (
        <BulkAssignDialog
          users={users.filter((u) => !u.deactivated)}
          taskIds={Array.from(selected)}
          onClose={() => setShowBulkAssign(false)}
          onDone={() => { setShowBulkAssign(false); setSelected(new Set()); load(); }}
        />
      )}
    </div>
  );
}

const inputCls = "mt-0.5 px-2 py-1.5 border border-gray-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1F3864]/30";

function Filt({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      {children}
    </label>
  );
}

function ViewTab({ active, onClick, icon: Icon, label, testid, badgeText }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-2 pb-2 px-3 -mb-px border-b-2 text-sm font-medium transition-colors ${
        active ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      {badgeText && (
        <span className="text-[9px] uppercase tracking-widest bg-[#E2E8F0] text-[#495057] px-1.5 py-0.5 rounded-sm font-semibold">{badgeText}</span>
      )}
    </button>
  );
}

function NewTaskDialog({ enums, users, onClose, onCreated }) {
  const [mode, setMode] = useState("case"); // 'case' | 'standalone'
  const [cases, setCases] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
    service_case_id: "",
    responsible_user_id: "",
    responsible_department: "Aftersales",
    priority: "Normal",
    deadline: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16),
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/service-cases?open_only=true&limit=200").then((r) => setCases(r.data));
  }, []);

  const filteredCases = useMemo(() => {
    if (!search) return cases.slice(0, 50);
    const q = search.toLowerCase();
    return cases
      .filter((c) =>
        (c.number || "").toLowerCase().includes(q) ||
        (c.customer_name || "").toLowerCase().includes(q) ||
        (c.vehicle_registration || "").toLowerCase().includes(q)
      )
      .slice(0, 50);
  }, [cases, search]);

  const submit = async () => {
    setError("");
    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!form.responsible_user_id) {
      setError("Responsible user is required.");
      return;
    }
    if (!form.deadline) {
      setError("Deadline is required.");
      return;
    }
    if (mode === "case" && !form.service_case_id) {
      setError("Select a linked Service Case (or switch to Standalone).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        responsible_user_id: form.responsible_user_id,
        responsible_department: form.responsible_department,
        priority: form.priority,
        deadline: new Date(form.deadline).toISOString(),
        service_case_id: mode === "case" ? form.service_case_id : null,
        linked_module: mode === "case" ? "service_case" : "admin",
      };
      await api.post("/tasks", payload);
      onCreated();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-task-dialog">
        <div className="px-5 py-3 border-b border-gray-200 bg-[#F4F6F8]">
          <h2 className="font-heading text-lg font-bold">New task</h2>
          <div className="text-xs text-[#6C757D] mt-1">Tasks drive the daily operations of every team.</div>
        </div>

        <div className="px-5 pt-4">
          <div className="inline-flex bg-[#F4F6F8] border border-gray-200 rounded-sm p-0.5">
            <button
              onClick={() => setMode("case")}
              data-testid="task-mode-case"
              className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${mode === "case" ? "bg-white shadow-sm text-[#1F3864]" : "text-[#6C757D]"}`}
            >
              Linked to Service Case
            </button>
            <button
              onClick={() => setMode("standalone")}
              data-testid="task-mode-standalone"
              className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${mode === "standalone" ? "bg-white shadow-sm text-[#1F3864]" : "text-[#6C757D]"}`}
            >
              Standalone admin task
            </button>
          </div>
        </div>

        <div className="p-5 space-y-3">
          {mode === "case" && (
            <div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Search service case</span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Number, customer or plate…"
                  className={`w-full ${inputCls}`}
                  data-testid="task-case-search"
                />
              </label>
              <label className="block mt-2">
                <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Linked service case *</span>
                <select
                  value={form.service_case_id}
                  onChange={(e) => setForm({ ...form, service_case_id: e.target.value })}
                  className={`w-full ${inputCls}`}
                  data-testid="task-case-select"
                >
                  <option value="">Select…</option>
                  {filteredCases.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.number} — {c.customer_name} ({c.vehicle_registration})
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {mode === "standalone" && (
            <div className="p-3 bg-[#F4F6F8] border border-gray-200 rounded-sm text-xs text-[#6C757D]">
              Standalone tasks are not linked to a Service Case. Use them for internal admin work (audits, training, reports, system setup).
            </div>
          )}

          <Input label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="task-title" />
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Description</span>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`w-full ${inputCls} h-16`} />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Responsible *</span>
              <select
                value={form.responsible_user_id}
                onChange={(e) => setForm({ ...form, responsible_user_id: e.target.value })}
                className={`w-full ${inputCls}`}
                data-testid="task-responsible"
              >
                <option value="">Select…</option>
                {users.filter((u) => !u.deactivated).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role.replaceAll("_", " ")})
                  </option>
                ))}
              </select>
            </label>
            <Input label="Department" value={form.responsible_department} onChange={(v) => setForm({ ...form, responsible_department: v })} />
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Priority</span>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={`w-full ${inputCls}`}>
                {(enums.priorities || []).map((p) => <option key={p}>{p}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Deadline *</span>
              <input
                type="datetime-local"
                value={form.deadline}
                onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                className={`w-full ${inputCls}`}
                data-testid="task-deadline"
              />
            </label>
          </div>

          {error && (
            <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="task-error">
              {error}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancel</button>
          <button onClick={submit} disabled={submitting} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60" data-testid="task-submit">
            {submitting ? "Creating…" : "Create task"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className={`w-full ${inputCls}`} data-testid={testid} />
    </label>
  );
}

function BulkAssignDialog({ users, taskIds, onClose, onDone }) {
  const [responsible, setResponsible] = useState("");
  const [comment, setComment] = useState("Bulk reassignment");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const submit = async () => {
    setErr("");
    if (!responsible) { setErr("Pick a user to reassign to."); return; }
    if (!comment.trim()) { setErr("Comment is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/bulk-assign", { task_ids: taskIds, responsible_user_id: responsible, comment });
      setResult(data);
      setTimeout(onDone, 800);
    } catch (e) {
      setErr(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="bulk-assign-dialog">
        <div className="px-5 py-3 border-b border-gray-200">
          <h2 className="font-heading text-lg font-bold">Bulk reassign {taskIds.length} task{taskIds.length === 1 ? "" : "s"}</h2>
          <div className="text-xs text-[#6C757D] mt-1">Notifications and audit entries will be created for each task.</div>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Reassign to *</span>
            <select value={responsible} onChange={(e) => setResponsible(e.target.value)} className={`w-full ${inputCls}`} data-testid="bulk-assign-user">
              <option value="">Select…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{`${u.name} (${(u.role || "").replaceAll("_", " ")})`}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">Comment *</span>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} className={`w-full ${inputCls} h-16`} data-testid="bulk-assign-comment" />
          </label>
          {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] rounded-sm text-sm" data-testid="bulk-assign-error">{err}</div>}
          {result && <div className="px-3 py-2 bg-[#D4EDDA] text-[#155724] rounded-sm text-sm" data-testid="bulk-assign-result">Reassigned {result.updated} · skipped {result.failed.length}</div>}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8]">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 text-sm bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm disabled:opacity-60" data-testid="bulk-assign-submit">
            {busy ? "Reassigning…" : "Reassign"}
          </button>
        </div>
      </div>
    </div>
  );
}

